import { COMPANION_WORKSHOP_PATH } from './companionWorkshop';
import { getUiLocale, setUiLocale, t, useUiLocale } from '@/i18n/locale';
import { loadBuddyDraft, saveBuddyDraft, clearBuddyDraft } from './companionDraft';
import type { BuddyProfile } from './companionApi';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMe } from '@/auth/useAuth';
import { companionEligible } from './CompanionRoutes';
import { useCompanion } from './CompanionContext';
import { buddyProfileSchema, defaultBuddy } from './companionApi';
import './companion.css';

const CHANNEL = 'airbotix-companion-design-v1';
export function CompanionDesignRoute() {
  useUiLocale();
  const me = useMe('kid');
  if (me.isPending) return <p className="p-6">{t('Opening your workshop…')}</p>;
  if (me.data?.kind !== 'kid' || !companionEligible(me.data))
    return (
      <div className="p-6">
        <p>{t('This buddy space is available on a family account.')}</p>
        <Link to="/learn" className="btn-pill-ghost">
          {' '}
          {t('Back to Learn')}{' '}
        </Link>
      </div>
    );
  return <CompanionDesign key={me.data.sub} />;
}

/** Host the approved workshop unchanged; account/save operations stay in the platform. */
export function CompanionDesign() {
  const locale = useUiLocale();
  const session = useCompanion();
  const latest = useRef(session);
  latest.current = session;
  const frame = useRef<HTMLIFrameElement>(null);
  const saving = useRef(false);
  const initialized = useRef(false);
  const draftBase = useRef<BuddyProfile | null>(null);
  const [ready, setReady] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const publicPath =
    location.pathname.startsWith('/try/') || location.pathname.endsWith('companion-preview.html')
      ? location.pathname
      : null;
  useEffect(() => {
    let active = true;
    const reply = (type: string, extra = {}) =>
      frame.current?.contentWindow?.postMessage(
        { channel: CHANNEL, type, ...extra },
        window.location.origin,
      );
    const receive = async (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== frame.current?.contentWindow ||
        event.data?.channel !== CHANNEL
      )
        return;
      const message = event.data;
      if (message.type === 'ready') {
        initialized.current = false;
        setReady((value) => value + 1);
        return;
      }
      if (message.type === 'language') {
        if (message.language === 'en' || message.language === 'zh') setUiLocale(message.language);
        return;
      }
      if (message.type === 'draft') {
        const persisted = saveBuddyDraft(latest.current.kidId, draftBase.current, message.profile);
        reply('draft-stored', { draftVersion: message.draftVersion, persisted });
        return;
      }
      if (message.type === 'exit') {
        navigate(publicPath || latest.current.returnTo, { state: latest.current.returnState });
        return;
      }
      if (message.type === 'open-chat') {
        if (latest.current.snapshot?.profile) navigate(publicPath || '/learn/companion');
        return;
      }
      if (message.type !== 'save' || saving.current || typeof message.requestId !== 'string')
        return;
      const profile = buddyProfileSchema.safeParse(message.profile);
      const current = latest.current;
      if (!profile.success || !current.snapshot) {
        reply('error', { requestId: message.requestId });
        return;
      }
      saving.current = true;
      try {
        const next = await current.client.profile(
          current.kidId,
          current.snapshot.revision,
          profile.data,
        );
        if (!active) return;
        const savedProfile = buddyProfileSchema.parse(next.profile);
        clearBuddyDraft(current.kidId);
        current.update(next);
        reply('saved', { requestId: message.requestId, profile: savedProfile });
      } catch {
        if (active) reply('error', { requestId: message.requestId });
      } finally {
        saving.current = false;
      }
    };
    window.addEventListener('message', receive);
    return () => {
      active = false;
      window.removeEventListener('message', receive);
    };
  }, [navigate, session.kidId, publicPath]);
  useEffect(() => {
    if (!ready || !session.snapshot || initialized.current) return;
    initialized.current = true;
    draftBase.current = session.snapshot.profile;
    frame.current?.contentWindow?.postMessage(
      {
        channel: CHANNEL,
        type: 'init',
        language: getUiLocale(),
        platformNavigation: !publicPath,
        profile: loadBuddyDraft(session.kidId, session.snapshot.profile) ||
          session.snapshot.profile || {
            ...defaultBuddy,
            name: getUiLocale() === 'zh' ? '团团' : 'Tuan Tuan',
            hat: 'none',
          },
      },
      window.location.origin,
    );
  }, [ready, session.snapshot, session.kidId, publicPath]);
  useEffect(() => {
    if (!ready) return;
    frame.current?.contentWindow?.postMessage(
      { channel: CHANNEL, type: 'language', language: locale },
      window.location.origin,
    );
  }, [locale, ready]);
  if (session.loadError)
    return (
      <div className="p-6">
        <p role="alert">{t('Your buddy could not load. Your saved look is still safe.')}</p>
        <button className="btn-pill-primary mt-4" onClick={() => void session.refresh()}>
          {' '}
          {t('Try again')}{' '}
        </button>
        <Link className="btn-pill-ghost" to={session.returnTo} state={session.returnState}>
          {session.returnTo.includes('/class')
            ? t('← Back to class')
            : session.returnTo === '/learn'
              ? t('Back to Learn')
              : t('← Back to page')}
        </Link>
      </div>
    );
  const returnLabel = session.returnTo.includes('/class')
    ? t('← Back to class')
    : session.returnTo === '/learn'
      ? t('← Learn')
      : t('← Back to page');
  return (
    <div className="flex h-full min-h-0 flex-col">
      {!publicPath && (
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-hairline bg-canvas-pure px-4 py-2 text-sm">
          <button
            type="button"
            className="btn-pill-ghost"
            onClick={() => {
              if (!ready) navigate(session.returnTo, { state: session.returnState });
              else
                frame.current?.contentWindow?.postMessage(
                  { channel: CHANNEL, type: 'exit-request' },
                  window.location.origin,
                );
            }}
          >
            {returnLabel}
          </button>
          <span className="font-bold text-ink">{t('Dress my buddy')}</span>
        </header>
      )}
      <iframe
        ref={frame}
        title={t('Buddy dressing workshop')}
        src={COMPANION_WORKSHOP_PATH}
        className="block min-h-0 w-full flex-1 border-0"
      />
    </div>
  );
}
